import { forwardRef } from "react";
import {
  InteractiveCardProps,
  ActionLinkProps,
  ActionButtonProps,
} from "./types";
import { Card } from "../../../components/Card";

import cx from "classnames";
import styles from "./InteractiveCard.module.scss";

/**
 * InteractiveCard component for creating clickable cards with nested interactive elements.
 *
 * Features:
 * - Clickable card area that can be a button or link
 * - Supports nested interactive elements without accessibility violations
 * - Uses sibling structure with pointer-events to avoid nested button issues
 * - Box-shadow feedback on hover and focus of any interactive element
 * - Full keyboard accessibility with proper focus management
 * - Touch-friendly with mobile support
 * - Customizable padding and background variants via contentProps
 * - WCAG AA 2.2 compliant
 * - Required ARIA labels for screen reader support
 * - Supports all Card props for styling and layout via contentProps
 *
 * @example
 * // Button card with nested actions
 * <InteractiveCard
 *   wrapperProps={{ "aria-label": "Kitchen Measurement 2 card" }}
 *   actionProps={{
 *     "aria-label": "Expand Kitchen Measurement 2",
 *     onClick: handleExpand,
 *   }}
 *   contentProps={{ padding: "large" }}
 * >
 *   <Text variant="headline">Kitchen Measurement 2</Text>
 *   <Button>Exact Size</Button>
 *   <Button>Block Size</Button>
 * </InteractiveCard>
 *
 * @example
 * // Link card with nested actions
 * <InteractiveCard
 *   wrapperProps={{ "aria-label": "Tommy Lee's Project card" }}
 *   actionProps={{
 *     "aria-label": "Navigate to Tommy Lee's Project",
 *     href: "/project/123",
 *   }}
 *   contentProps={{ padding: "large" }}
 * >
 *   <Text variant="headline">Tommy Lee's Project</Text>
 *   <IconButton icon={Edit} onClick={(e) => {
 *     e.preventDefault();
 *     handleEdit();
 *   }} />
 * </InteractiveCard>
 */
export const InteractiveCard = forwardRef<HTMLDivElement, InteractiveCardProps>(
  (props, ref) => {
    const {
      wrapperProps,
      actionProps,
      contentProps = {},
      children,
      "data-interactive": dataInteractive,
    } = props as InteractiveCardProps & {
      "data-interactive"?: string;
    };

    // Destructure className and style from wrapperProps to merge with internal values
    const {
      "aria-label": ariaLabel,
      className: wrapperClassName,
      style: wrapperStyle,
      ...restWrapperProps
    } = wrapperProps;

    // Destructure aria-label from actionProps
    const { "aria-label": actionAriaLabel, ...restActionProps } = actionProps;

    // Destructure contentProps
    const {
      padding = "medium",
      background,
      className: contentClassName,
      ...restContentProps
    } = contentProps;

    const actionClassNames = cx(styles["card-action"], {
      [styles[`bg-${background}`]]: !!background,
    });

    const contentClassNames = cx(styles["card-content"], contentClassName);

    const wrapperClassNames = cx(styles["card-wrapper"], wrapperClassName);

    // Determine the interactive element type based on href presence
    const isLink = "href" in restActionProps;

    return (
      <div
        {...restWrapperProps}
        role="group"
        aria-label={ariaLabel}
        className={wrapperClassNames}
        style={wrapperStyle}
        ref={ref}
      >
        {/* Interactive layer (button or link) - provides background and hover effects */}
        {isLink ? (
          <a
            {...(restActionProps as ActionLinkProps)}
            aria-label={actionAriaLabel}
            className={actionClassNames}
            data-interactive={dataInteractive}
          />
        ) : (
          <button
            {...(restActionProps as ActionButtonProps)}
            aria-label={actionAriaLabel}
            className={actionClassNames}
            data-interactive={dataInteractive}
            type="button"
          />
        )}

        {/* Content layer - transparent card with padding */}
        <Card
          padding={padding}
          className={contentClassNames}
          {...restContentProps}
        >
          {children}
        </Card>
      </div>
    );
  },
);

InteractiveCard.displayName = "InteractiveCard";
